/* --------------------------------------------------------------------------
   TT-PDP - the Pangea product configurator.

   A port of the mockup's DCLogic component (the <script type="text/x-dc"> block
   at the foot of design/zola/mockups/Pangea 4x6 PDP.dc.html) to vanilla JS
   against real Shopify data. Same state shape, same derived values, same
   names - so the two can be diffed line by line when the mockup moves.

   WHAT IS DIFFERENT FROM THE MOCKUP, and why:

   1. The mockup hardcodes IMGS, LEGS, COLS, INC and ESS as literals. Here they
      all arrive from Shopify, serialised by sections/tt-pdp.liquid into a
      <script type="application/json"> block. Nothing about the product is in
      this file.
   2. The mockup re-renders through React. This does not: Liquid renders the
      full DOM once, and the only job here is to repaint the pieces that depend
      on state. That keeps the markup - the thing being matched pixel for pixel
      - in one place, and it means the page is complete and correct before a
      single byte of JS runs.
   3. "Pre-order Now" / "Add to Cart" posts a real /cart/add.js with every
      selected line: table, bundle, chairs, essentials.

   The repaint contract, all driven off data- attributes Liquid emits:

     data-tt-bind="key"       textContent  <- vals[key]
     data-tt-label="key"      first TEXT NODE only <- vals[key]
     data-tt-html="key"       innerHTML    <- vals[key]
     data-tt-if="key"         shown when vals[key] is truthy
     data-tt-show="value"     the display an if-element takes when shown;
                              omit for block, required for flex/grid/inline
     data-tt-style="prop:key" style[prop]  <- vals[key]
     data-tt-class="key"      className    <- base + ' ' + vals[key]
     data-tt-pick="group:i"   click -> select index i of group

   Keep those five in sync with renderVals() below and nothing else needs to
   know how the page is composed.
   -------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* PRICES IN THE SHOPPER'S CURRENCY. This printed '$' and two decimals whatever the
     currency, so a Japan visitor's ¥337,063 read "$337,063.00" (Thomas, 2026-10-20).
     The section hands over Shopify's own rendering of 1,234,567.89 in the active
     currency (window.ttMoneySample: "$1,234,567.89", "€1.234.567,89", "1.234.567,89 kr",
     "¥1,234,568"), and this copies its symbol, separators and decimals. */
  var MONEY = (function (s) {
    s = String(s || '$1,234,567.89');
    var a = s.search(/\d/), z = s.length - 1;
    while (z >= 0 && !/\d/.test(s.charAt(z))) z--;
    var body = s.slice(a, z + 1);
    var dec = /[^\d](\d{2})$/.test(body) ? body.charAt(body.length - 3) : '';
    var whole = dec ? body.slice(0, -3) : body;
    var sep = (whole.match(/\D/) || [''])[0];
    return { pre: s.slice(0, a), post: s.slice(z + 1), dec: dec, sep: sep };
  })(window.ttMoneySample);

  function fmt(n, wholeOnly) {
    var places = MONEY.dec && !wholeOnly ? 2 : 0;
    var parts = Number(n).toFixed(places).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, MONEY.sep);
    return MONEY.pre + whole + (places ? MONEY.dec + parts[1] : '') + MONEY.post;
  }

  /* The mockup's two selection palettes, the only literals left. They are the
     mockup's own hexes; tokenising them is a separate, one-value-at-a-time pass
     (see the note at the head of sections/tt-home-mock.liquid). */
  var C = {
    pickBg: 'var(--z-pick-bg)',
    pickBgOff: 'var(--z-panel)',
    fg: 'var(--z-ink)',
    fgOff: 'var(--z-muted)',
    rule: 'var(--z-ink)',
    none: 'transparent',
    div: 'var(--z-divider)',
    accent: 'var(--z-accent-1)',
    onDark: 'var(--z-panel)',
    amberText: 'var(--z-amber-ink)',
    mute: 'var(--z-quiet)',
    thumbOff: 'var(--z-numeral-pale)',
    essOn: 'var(--z-pick-on)',
    essOff: 'var(--z-pick-bg)',
    essHoverOff: 'var(--z-pick-hover)'
  };

  /* THE SELECTED OPTION IS THE ACCENT, like the Signature Bundle row. Josh,
     2026-08-20: "Height, Finish, Include Table Toppers options - make the
     selected options the accent orange like the Signature bundle selected
     option." It was a paper tint with an ink rule, which reads as hover rather
     than as chosen, and on a three-cell row nothing told you which one you had.

     Accent on white type is the DEFAULT of the three sanctioned pill schemes in
     design/zola/README.md, and it is exactly what the bundle row already uses -
     so this makes two controls agree rather than inventing a third look.

     `accent` is a parameter rather than the only behaviour because the chairs
     quantity stepper in the seating drawer calls this too, and Josh named three
     controls. Flip its call to true if it should match. */
  function cell(isOn, i, accent) {
    return {
      bg: isOn ? (accent ? C.accent : C.pickBg) : C.pickBgOff,
      fg: isOn ? (accent ? C.onDark : C.fg) : C.fgOff,
      rule: isOn ? (accent ? C.accent : C.rule) : C.none,
      /* A CHIP HAS FOUR BORDERS, a segmented cell has one. `rule` is the
         underline the old full-width bar drew and `div` is the seam between two
         cells in a grid; neither describes a control that stands on its own, so
         a chip binding built from them drew an orange line under a white box.
         Kept beside them rather than replacing them because the Bundle Size row
         and the chairs stepper are still segmented. */
      bord: isOn ? (accent ? C.accent : C.rule) : C.div,
      div: i === 0 ? C.none : C.div,
      ml: i === 0 ? '0' : '-1px'
    };
  }

  function Pdp(root) {
    this.root = root;
    var node = root.querySelector('[data-tt-pdp-data]');
    this.d = JSON.parse(node.textContent);

    /* THE BUNDLE ITEMS RIDE IN THEIR OWN NODE, not in the boot JSON, because
       they are assembled inside snippets/tt-pdp-drawer.liquid - the same loop
       that draws the comparison rows - and the boot JSON is written by the
       section. Building them twice in two files is how the two would drift into
       disagreeing about which row is index 3. Absent on every product with no
       bundle, which is why this is guarded rather than assumed. */
    this.bItems = [];
    var bNode = root.querySelector('[data-tt-bundle-items]');
    if (bNode) {
      try { this.bItems = JSON.parse(bNode.textContent) || []; } catch (e) { this.bItems = []; }
    }

    this.state = {
      img: 0,
      leg: 0,
      col: 0,
      /* HOW MANY. Josh, 2026-08-18: "missing quantity option... customers
         sometimes purchase more than 1 at a time." It multiplies the PRODUCT
         line only - a bundle or a set of chairs is chosen by its own control and
         adding four tables should not silently add four bundles too. */
      qty: 1,
      /* Hull only (0) or with the pedal drive (1) - a boat's package, present
         only when the boot data carries a `drive` (sections/tt-pdp.liquid). Boats
         open With the drive (Josh, 2026-10-20: pkgDefault 1). */
      pkg: this.d.drive ? Number(this.d.pkgDefault) || 0 : 0,
      /* which model is picked (sections/tt-pdp.liquid `model` blocks); 0 is this product */
      model: 0,
      toppers: 1,
      bundle: 0,
      drawer: 0,
      drawerClosing: 0,
      /* Which bundle item's detail panel is open, by its index in the
         comparison table's union list. -1 is closed rather than 0, because 0 is
         a real row. */
      binfo: -1,
      chairs: 0,
      draftChairs: 2,
      q: {},
      lb: false,
      /* The overlay's own index, kept SEPARATE from `img`. The buy box shows
         product.media and the gallery strip shows the curated list; sharing one
         index let the buy box's arrows walk out of the product photography and
         into the strip's pictures. `lbSet` says which list the overlay is
         currently showing. */
      lbImg: 0,
      lbSet: 'main',
      zoom: false,
      fade: false,
      adding: false
    };

    this.binds = [];
    this.collect();
    this.bindQty();
    this.wire();

    /* THE URL DECIDES THE STARTING VARIANT, before the first paint. Applied
       straight to state rather than through set(), because set() would write the
       URL back at boot and, on a link carrying a variant that no longer exists,
       would rewrite a shopper's address bar to something they did not ask for.
       An unrecognised or absent `?variant=` simply leaves the defaults alone. */
    /* No colour in the link: start on the variant Liquid picked as the first
       one with stock (Josh, 2026-10-16), photo included. */
    if (this.d.defaultKey) {
      var dk = String(this.d.defaultKey).split('|');
      this.state.leg = Number(dk[0]) || 0;
      this.state.col = Number(dk[1]) || 0;
      var dv = this.d.variants[this.d.defaultKey];
      if (dv && typeof dv.img === 'number' && dv.img >= 0) this.state.img = dv.img;
    }

    var want = this.fromUrl();
    if (want) {
      this.state.leg = want.leg;
      this.state.col = want.col;
      this.state.toppers = want.toppers;
      var wv = this.d.variants[want.leg + '|' + want.col];
      if (wv && typeof wv.img === 'number' && wv.img >= 0) this.state.img = wv.img;
    }
    /* a link that names a package (?package=hull) opens on it */
    var wantPkg = this.pkgFromUrl();
    if (wantPkg !== null) this.state.pkg = wantPkg;
    /* the opening picture belongs to the opening colour and package */
    if (this.d.imgTags && !this.shows(this.state.img)) this.state.img = this.visImgs()[0];

    this.repaint();
  }

  /* One pass over the DOM at boot, so a repaint is a walk of an array rather
     than a fresh querySelectorAll of the whole page. There are ~400 bound nodes
     on this template; re-querying them on every click is what makes a
     configurator feel slow. */
  Pdp.prototype.collect = function () {
    var self = this;
    var sel =
      '[data-tt-bind],[data-tt-label],[data-tt-html],[data-tt-if],[data-tt-style],[data-tt-class],' +
      '[data-tt-value],[data-tt-disabled]';
    this.root.querySelectorAll(sel).forEach(function (el) {
      var entry = { el: el };
      if (el.dataset.ttBind) entry.bind = el.dataset.ttBind;
      /* An <input> carries its text in .value, not in a child node, so
         data-tt-bind would write a textContent nobody can see and leave the
         field showing whatever it was rendered with. */
      if (el.dataset.ttValue) entry.value = el.dataset.ttValue;
      if (el.dataset.ttDisabled) entry.disabled = el.dataset.ttDisabled;
      /* Not the same as data-tt-bind. The CTA is `Add to Cart<span>→</span>` -
         a text node beside an element - so writing textContent would delete the
         arrow the mockup draws. This rewrites the text node and leaves the
         element children alone. */
      if (el.dataset.ttLabel) entry.label = el.dataset.ttLabel;
      if (el.dataset.ttHtml) entry.html = el.dataset.ttHtml;
      if (el.dataset.ttIf) {
        entry.cond = el.dataset.ttIf;
        /* THE DISPLAY VALUE THIS ELEMENT WANTS WHEN SHOWN, captured before
           anything is toggled.

           Showing used to be `el.style.display = ''`, which does not mean
           "default" - it DELETES the inline declaration. Every hidden element
           here is written `hidden style="display: none; ...rest"`, so for
           anything whose real display is inline the value being deleted IS the
           one it needs back, and it fell to the UA default instead.

           That broke the whole lightbox. Its root is a `display: none` that has
           to become FLEX - the overlay is a flex column, and the hero sits in a
           `flex: 1; min-height: 0` cage. Restored as block, the column direction
           went inert, the hero lost its cage and painted at natural size off the
           right of the viewport, and both thumbnail rails - also flex - stacked
           their thumbnails vertically down the page.

           `data-tt-show` names the value explicitly where it is not block. */
        entry.show = el.dataset.ttShow || '';
      }
      if (el.dataset.ttClass) {
        entry.cls = el.dataset.ttClass;
        /* The base class list is DECLARED, not inferred from what Liquid
           rendered. Liquid already writes the selected thumbnail's `is-on`, so
           reading className back at boot and appending the state class again
           produced "pdp-thumb is-on is-on" - harmless to render, and a real
           difference in the tree. */
        entry.baseCls = el.dataset.ttBase || el.className;
      }
      if (el.dataset.ttStyle) {
        entry.styles = el.dataset.ttStyle.split(';').map(function (pair) {
          var bits = pair.split(':');
          return { prop: bits[0].trim(), key: bits[1].trim() };
        });
      }
      self.binds.push(entry);
    });
  };

  /* Whichever gallery the overlay was opened from. THREE now: the buy box's
     product media, the curated strip, and every photograph customers attached
     to a review. Separate lists and one index each, for the reason recorded on
     `lbImg` above - a shared index let the arrows walk out of one gallery and
     into another. */
  Pdp.prototype.lbList = function () {
    if (this.state.lbSet === 'strip') return this.d.stripImages || [];
    if (this.state.lbSet === 'review') return this.d.reviewImages || [];
    return this.d.images;
  };

  /* THE VARIANT RIDES IN THE URL, AND ONLY THE VARIANT. Josh, 2026-08-21: "I
     was on a table PDP page selected a bunch of options... navigated to a
     different page came back and everything was reset if possible save the
     settings... unless you think that's a bad idea and not typical ecomm
     practice."

     Half of it is a bad idea and half of it is the standard. Finish and height
     ARE the variant, and every Shopify store puts the variant in `?variant=` -
     so back, forward, refresh, a bookmark and a pasted link all keep them, which
     is what a shopper means by "where I left it". The optional add-ons are
     money: coming back to a page silently holding a $766 bundle you had half
     chosen is how people buy things they did not mean to, so those reset and the
     restore notice below says as much when there were any.

     replaceState, not pushState: choosing a finish is not a page a Back button
     should have to walk through. */
  Pdp.prototype.syncUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    var v = this.variant();
    if (!v || !v.id) return;
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.get('variant') === String(v.id)) return;
      u.searchParams.set('variant', String(v.id));
      window.history.replaceState({}, '', u.toString());
    } catch (e) { /* an exotic URL is not worth breaking the configurator for */ }
  };

  /* THE PACKAGE IN THE LINK (2026-10-20). Written only when it is not the one the
     page opens on, so a plain product link stays plain: ?package=hull (Hull only /
     Kayak only), drive, or two-drives. Read once at boot; null when absent. */
  var PKG_WORDS = ['hull', 'drive', 'two-drives'];
  Pdp.prototype.pkgFromUrl = function () {
    if (!this.d.drive) return null;
    var w;
    try { w = new URL(window.location.href).searchParams.get('package'); }
    catch (e) { return null; }
    var i = PKG_WORDS.indexOf(w);
    if (i < 0 || (i === 2 && !this.d.drive2)) return null;
    return i;
  };
  Pdp.prototype.syncPkgUrl = function () {
    if (!this.d.drive || !window.history || !window.history.replaceState) return;
    try {
      var u = new URL(window.location.href);
      var def = Number(this.d.pkgDefault) || 0;
      var want = this.state.pkg === def ? null : PKG_WORDS[this.state.pkg];
      if (u.searchParams.get('package') === want) return;
      if (want) u.searchParams.set('package', want);
      else u.searchParams.delete('package');
      window.history.replaceState({}, '', u.toString());
    } catch (e) { /* an exotic URL is not worth breaking the configurator for */ }
  };

  /* Which leg/colour pair the URL is asking for, or null. Read once at boot. */
  Pdp.prototype.fromUrl = function () {
    var want;
    try { want = new URL(window.location.href).searchParams.get('variant'); }
    catch (e) { return null; }
    if (!want) return null;
    var map = this.d.variants || {};
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(map, k) && String(map[k].id) === String(want)) {
        var bits = k.split('|');
        return { leg: Number(bits[0]), col: Number(bits[1]), toppers: bits[2] === 'n' ? 0 : 1 };
      }
    }
    return null;
  };

  Pdp.prototype.set = function (patch) {
    Object.assign(this.state, patch);
    if (patch.leg !== undefined || patch.col !== undefined || patch.toppers !== undefined) this.syncUrl();
    if (patch.pkg !== undefined) this.syncPkgUrl();
    this.repaint();
  };

  Pdp.prototype.wire = function () {
    var self = this;
    var s = this.state;

    this.root.addEventListener('click', function (ev) {
      var el = ev.target.closest('[data-tt-pick],[data-tt-act]');
      if (!el || !self.root.contains(el)) return;

      if (el.dataset.ttPick) {
        var bits = el.dataset.ttPick.split(':');
        ev.preventDefault();
        self.pick(bits[0], Number(bits[1]));
        return;
      }

      var act = el.dataset.ttAct;
      var arg = el.dataset.ttArg;
      ev.preventDefault();
      /* Rows open a drawer on click, but the Remove and the +/- inside them are
         their own actions - the mockup stops propagation on exactly these. */
      if (/^(removeChairs|removeEss|essPlus|essMinus)$/.test(act)) ev.stopPropagation();
      /* THE ELEMENT, as a third argument. One action needs to know WHICH row it
         was fired from - "Read more" appears once per long review and there is
         no index to give it that is not bookkeeping the markup would have to
         keep in step. Every other action ignores it. */
      self.act(act, arg, el);
    });

    document.addEventListener('keydown', function (ev) {
      /* ESCAPE CLOSES THE INNERMOST THING FIRST, and this branch sits ABOVE the
         lightbox guard below because that guard returns early on every key when
         the overlay is shut - which is why the drawer has never answered Escape
         at all. The item panel closes back to the comparison table rather than
         to the page, because the shopper came to look at one line of it. */
      if (ev.key === 'Escape' && !self.state.lb) {
        if (self.state.binfo >= 0) return self.set({ binfo: -1 });
        if (self.state.drawer) return self.closeDrawer();
      }
      if (!self.state.lb) return;
      if (ev.key === 'Escape') self.set({ lb: false, zoom: false });
      var n = self.lbList().length;
      if (!n) return;
      /* The keyboard is a route to the next image too, and a fix that only
         covered the on-screen arrows would leave the zoom stuck for anyone
         using the keys - the people most likely to be stepping quickly. */
      if (ev.key === 'ArrowRight') self.set({ lbImg: (s.lbImg + 1) % n, zoom: false, fade: !s.fade });
      if (ev.key === 'ArrowLeft') self.set({ lbImg: (s.lbImg + n - 1) % n, zoom: false, fade: !s.fade });
    });

    var form = this.root.querySelector('[data-tt-pdp-form]');
    if (form) form.addEventListener('submit', function (ev) { self.submit(ev); });
  };

  /* The image index for the variant the state WOULD select once `next` is
     applied. Read before this.set so it can travel in the same update - two
     set() calls would render twice and cross-fade against itself. */
  /* THE GALLERY FOLLOWS THE PICK (2026-10-19). With `imgTags` in the boot data
     (sections/tt-pdp.liquid, gallery_by_color), a picture shows only while its
     colour and package match the shopper's: "" is any colour, "any" either
     package, "hull" only Hull only, "drive" only With the drive. `st` is the state
     to test, so a pick can be checked before it is made. */
  Pdp.prototype.shows = function (i, st) {
    var t = this.d.imgTags && this.d.imgTags[i];
    if (!t) return true;
    st = st || this.state;
    var col = (this.d.colors[st.col] || {}).name || '';
    if (t.c && t.c !== col) return false;
    /* "drive" is any package with a drive; "drive2" only the tandems' both-drives one */
    if (t.p === 'drive' && !st.pkg) return false;
    if (t.p === 'drive2' && st.pkg !== 2) return false;
    if (t.p === 'hull' && st.pkg) return false;
    /* a model's pictures ("m0" this product, "m1".. each `model` block) show only while it is picked */
    if (/^m\d+$/.test(t.p) && +t.p.slice(1) !== (st.model || 0)) return false;
    return true;
  };
  Pdp.prototype.visImgs = function (st) {
    var out = [];
    for (var i = 0; i < this.d.images.length; i++) if (this.shows(i, st)) out.push(i);
    return out.length ? out : [0];
  };
  /* After a colour or package pick: if the picture on screen no longer belongs,
     go to the first one that does. */
  Pdp.prototype.keepImg = function (next) {
    if (!this.d.imgTags) return next;
    var st = Object.assign({}, this.state, next);
    var img = next.img === undefined ? this.state.img : next.img;
    if (!this.shows(img, st)) {
      /* the same place in the new set: the three-quarter view of Hull only becomes the
         three-quarter view With the drive, when both sets list their angles alike */
      var was = this.visImgs().indexOf(this.state.img);
      var vis = this.visImgs(st);
      next.img = was >= 0 && was < vis.length ? vis[was] : vis[0];
      next.fade = !this.state.fade;
    }
    return next;
  };
  Pdp.prototype.stepImg = function (cur, dir) {
    var vis = this.visImgs();
    var k = vis.indexOf(cur);
    if (k < 0) return vis[0];
    return vis[(k + dir + vis.length) % vis.length];
  };

  Pdp.prototype.withVarImg = function (next) {
    var s = this.state;
    var leg = next.leg === undefined ? s.leg : next.leg;
    var col = next.col === undefined ? s.col : next.col;
    var v = this.d.variants && this.d.variants[leg + '|' + col];
    if (v && typeof v.img === 'number' && v.img >= 0 && v.img !== s.img) {
      next.img = v.img;
      next.fade = !s.fade;
    }
    return next;
  };

  Pdp.prototype.pick = function (group, i) {
    var s = this.state;
    /* CHOOSING A VARIANT MOVES THE HERO TO ITS PHOTOGRAPH. Josh, 2026-08-21:
       "Selecting table variance needs to cycle to the correct image like the
       current live site."

       The index is resolved in Liquid off `featured_media.position` and rides in
       the variant map, so there is no URL matching at runtime and no second list
       to keep in step. `varImg` returns -1 when the variant has no image of its
       own - true of 46 of the 51 products - and the hero is then left exactly
       where the shopper had it, because jumping to frame one on every tap would
       be worse than not moving at all. */
    if (group === 'leg') return this.set(this.withVarImg({ leg: i }));
    if (group === 'col') return this.set(this.keepImg(this.withVarImg({ col: i })));
    if (group === 'top') return this.set({ toppers: i });
    if (group === 'pkg') return this.set(this.keepImg({ pkg: i }));
    /* another model: its own pictures, at the same place in the set where it has one */
    if (group === 'model') return this.set(this.keepImg({ model: i }));
    if (group === 'bundle') return this.set({ bundle: i });
    if (group === 'binfo') return this.set({ binfo: i });
    if (group === 'img') return this.set({ img: i, fade: !s.fade });
    if (group === 'chairs') return this.set({ draftChairs: i, chairs: i });
    /* A strip thumbnail opens the overlay on the CURATED list; it never touches
       the buy box's own index. */
    if (group === 'stripimg') return this.set({ lbSet: 'strip', lbImg: i, fade: !s.fade, lb: true, zoom: false });
    /* A review thumbnail opens the overlay on the REVIEW list, at that
       photograph's index in it. Josh, 2026-08-20: "make sure the pics show
       Thumbnails and open in the same pop up window carousel like the rest of
       the site" - so it is the same overlay, the same arrows, the same keys. */
    if (group === 'reviewimg') return this.set({ lbSet: 'review', lbImg: i, fade: !s.fade, lb: true, zoom: false });
    /* Picking a thumbnail INSIDE the overlay is the third route to a different
       photograph. Same rule: a new picture opens unzoomed. */
    if (group === 'lbmain') return this.set({ img: i, lbImg: i, zoom: false, fade: !s.fade });
    if (group === 'lbstrip') return this.set({ lbImg: i, zoom: false, fade: !s.fade });
    if (group === 'lbreview') return this.set({ lbImg: i, zoom: false, fade: !s.fade });
  };

  Pdp.prototype.act = function (act, arg, el) {
    var s = this.state;
    var self = this;
    var n = this.d.images.length;

    switch (act) {
      case 'openLb':
        return this.set({ lb: true, zoom: false, lbSet: 'main', lbImg: s.img });
      case 'closeLb':
        return this.set({ lb: false, zoom: false });
      case 'toggleZoom':
        return this.set({ zoom: !s.zoom });
      /* The arrows serve two galleries. Inside the overlay they walk whichever
         list it is showing; on the hero they walk the buy box's own. */
      /* STEPPING RESETS THE ZOOM. Josh, 2026-08-21: "if I click in zoom when I
         hit next to go to the next image, I'm still zoomed. It should reset to
         the standard view."

         Zoom is a property of looking at ONE photograph, not a mode the overlay
         is in. Carrying it across a step lands the next picture pre-cropped to
         wherever the last one happened to be magnified, which is never the part
         of the new image anybody wanted. Both branches, and the thumbnail picks
         below, so there is no route to the next image that keeps it. */
      case 'prevImg':
        if (s.lb) {
          var pl = this.lbList().length;
          if (this.d.imgTags && s.lbSet === 'main') return this.set({ lbImg: this.stepImg(s.lbImg, -1), zoom: false, fade: !s.fade });
          return pl ? this.set({ lbImg: (s.lbImg + pl - 1) % pl, zoom: false, fade: !s.fade }) : undefined;
        }
        if (this.d.imgTags) return this.set({ img: this.stepImg(s.img, -1), fade: !s.fade });
        return this.set({ img: (s.img + n - 1) % n, fade: !s.fade });
      case 'nextImg':
        if (s.lb) {
          var nl = this.lbList().length;
          if (this.d.imgTags && s.lbSet === 'main') return this.set({ lbImg: this.stepImg(s.lbImg, 1), zoom: false, fade: !s.fade });
          return nl ? this.set({ lbImg: (s.lbImg + 1) % nl, zoom: false, fade: !s.fade }) : undefined;
        }
        if (this.d.imgTags) return this.set({ img: this.stepImg(s.img, 1), fade: !s.fade });
        return this.set({ img: (s.img + 1) % n, fade: !s.fade });
      case 'openBundle':
        return this.set({ drawer: 'bundle' });
      case 'openSeating':
        return this.set({ drawer: 'seating', draftChairs: s.chairs });
      case 'openEss':
        return this.set({ drawer: 'ess' });
      case 'closeBinfo':
        return this.set({ binfo: -1 });
      case 'closeDrawer':
        /* Closing the drawer takes the item panel with it. Leaving `binfo` set
           would reopen the drawer straight into the panel next time, on a row
           the shopper had already finished with. */
        this.state.binfo = -1;
        return this.closeDrawer();
      case 'qtyPlus':
        return this.set({ qty: Math.min(99, s.qty + 1) });
      case 'qtyMinus':
        return this.set({ qty: Math.max(1, s.qty - 1) });
      /* THE TOPPERS CONTROL IS ONE BOX NOW, not a Yes/No pair. Josh, 2026-08-21:
         the buy box was 'too many colored rows'. Yes/No was a boolean wearing a
         variant picker's costume, and it was the cheapest of the three rows to
         delete outright. `top:1` / `top:0` still work - the drawer and any older
         markup can still set it by index - this only adds the toggle a single
         checkbox needs. */
      case 'toggleToppers':
        return this.set({ toppers: s.toppers === 0 ? 1 : 0 });
      case 'removeChairs':
        return this.set({ chairs: 0, draftChairs: 2 });
      case 'removeEss':
        return this.set({ q: {} });
      case 'chairPlus':
        return this.set({ draftChairs: s.draftChairs + 2, chairs: s.draftChairs + 2 });
      case 'chairMinus':
        return this.set({
          draftChairs: Math.max(0, s.draftChairs - 2),
          chairs: Math.max(0, s.draftChairs - 2)
        });
      case 'essPlus':
        return this.setQ(Number(arg), 1);
      case 'essMinus':
        return this.setQ(Number(arg), -1);
      case 'essToggle':
        return this.setQ(Number(arg), (s.q[arg] || 0) === 0 ? 1 : -(s.q[arg] || 0));
      case 'expandReview': {
        /* Josh, 2026-08-20: "Need option to read more."

           THE CLAMP IS INLINE, so this has to clear it inline. Everything in
           snippets/tt-pdp-reviews.liquid is inline-styled, and an inline
           declaration outranks any stylesheet - removing a class, or adding
           one, would do nothing at all here. Three properties go back, not
           one: -webkit-line-clamp alone leaves `display: -webkit-box` on the
           paragraph, which lays the text out in a flexbox and swallows the
           bottom margin.

           Scoped by walking up to the row rather than by an index, because an
           index would be a second thing the markup has to keep in step with
           the review it belongs to. */
        var row = el && el.closest ? el.closest('.tt-review') : null;
        var body = row ? row.querySelector('[data-review-body]') : null;
        if (body) {
          body.style.display = 'block';
          body.style.webkitLineClamp = 'unset';
          body.style.overflow = 'visible';
        }
        if (el && el.remove) el.remove();
        return;
      }
      case 'moreReviews': {
        /* Every review is already rendered; this reveals the rest. No request,
           no Judge.me script, works with the app's JS blocked. */
        this.root.querySelectorAll('.tt-review[hidden]').forEach(function (el) {
          el.hidden = false;
          el.style.display = '';
        });
        var link = this.root.querySelector('[data-tt-act="moreReviews"]');
        if (link) link.remove();
        return;
      }
      case 'toDims': {
        scrollToId('pdp-dimensions');
        return;
      }
      /* Josh, 2026-08-20: "Make clicking the review count or stars in the buy
         box smooth scroll to the reviews and not just jump to the anchor."
         Same landing as the Dimensions jump - 90px of clearance for the sticky
         nav, which a bare #anchor does not leave. */
      case 'toReviews': {
        scrollToId('reviews');
        return;
      }
      default:
        return;
    }
  };

  Pdp.prototype.setQ = function (i, delta) {
    var q = Object.assign({}, this.state.q);
    q[i] = Math.max(0, (q[i] || 0) + delta);
    this.set({ q: q });
  };

  /* The close animation has to finish before the node leaves the DOM, which is
     the one place this needs a timer. 320ms is the mockup's drawerOut. */
  Pdp.prototype.closeDrawer = function () {
    var self = this;
    if (this.state.drawerClosing) return;
    this.set({ drawerClosing: this.state.drawer });
    clearTimeout(this._dt);
    this._dt = setTimeout(function () {
      self.set({ drawer: 0, drawerClosing: 0 });
    }, 320);
  };

  /* ------------------------------------------------------------------------
     renderVals - the mockup's method of the same name, same keys, same order.
     ------------------------------------------------------------------------ */
  Pdp.prototype.renderVals = function () {
    var s = this.state;
    var d = this.d;
    var v = {};

    var topOn = s.toppers !== 0;
    var variant = this.variant();
    /* another model sells another product: its price */
    var model = s.model && d.models ? d.models[s.model] : null;
    var base = model ? model.price : (variant ? variant.price : 0);
    if (!topOn && d.topperDiscount && !d.hasNoTop) base -= d.topperDiscount;

    /* The product line scales with quantity; the add-ons do not. */
    var sub = base * s.qty;
    var save = 0;

    /* THE BOAT PACKAGE (2026-10-18). With the drive picked, one boat is one
       boat plus one drive, less the drive's bundle discount - the same sum the
       store's automatic discount makes at checkout, so the page and the
       checkout cannot disagree. Every product without a `drive` skips this. */
    var drv = d.drive && s.pkg ? d.drive : null;
    var drvEach = drv ? drv.price - drv.off : 0;
    /* the tandems' third package carries the second drive too */
    if (drv && s.pkg === 2 && d.drive2) drvEach += d.drive2.price - d.drive2.off;
    sub += drvEach * s.qty;

    var bundle = s.bundle ? d.bundles[String(s.bundle)] : null;
    if (bundle) {
      sub += bundle.price;
      save += Math.max(0, bundle.compareAt - bundle.price);
    }

    var chairPacks = Math.floor(s.chairs / 2);
    if (d.seating) {
      sub += chairPacks * d.seating.price;
      save += chairPacks * Math.max(0, d.seating.compareAt - d.seating.price);
    }

    var essN = 0;
    var essT = 0;
    d.essentials.forEach(function (e, i) {
      var q = s.q[i] || 0;
      essN += q;
      essT += q * e.price;
      sub += q * e.price;
      if (e.compareAt > e.price) save += q * (e.compareAt - e.price);
    });

    var dk = s.drawer || s.drawerClosing;
    var bAdded = s.bundle !== 0;

    /* ---- gallery ---- */
    var lbList = this.lbList();
    v.mainImg = d.images[s.img];
    v.lbImg = lbList[s.lbImg] || d.images[s.img];
    v.fadeAnim = s.fade ? 'ttPdpGalFadeA 0.4s ease' : 'ttPdpGalFadeB 0.4s ease';
    v.lbFadeAnim = s.fade ? 'ttPdpGalFadeA 0.35s ease' : 'ttPdpGalFadeB 0.35s ease';
    v.imgCounter = s.lbImg + 1 + ' / ' + lbList.length;
    /* a gallery that follows the pick counts only the picked set: 1 / 8, not 1 / 48 */
    if (d.imgTags && s.lbSet === 'main') {
      var lbVis = this.visImgs();
      v.imgCounter = (Math.max(0, lbVis.indexOf(s.lbImg)) + 1) + ' / ' + lbVis.length;
    }
    v.lbOpen = s.lb;
    v.lbMain = s.lbSet === 'main';
    v.lbStrip = s.lbSet === 'strip';
    v.lbReview = s.lbSet === 'review';
    v.lbDisplay = s.zoom ? 'block' : 'flex';
    /* PHONES OPEN FULL FRAME (Josh, 2026-10-19, iPhone): the 120px side gutters the
       desktop arrows sit in left a 390px screen a picture 150px wide. Below 750px the
       picture takes the whole width; the arrows float over it. */
    var lbNarrow = window.innerWidth < 750;
    v.lbMax = s.zoom ? 'none' : (lbNarrow ? '100%' : '92%');
    v.lbMaxH = s.zoom ? 'none' : '100%';
    v.lbW = s.zoom ? '160%' : 'auto';
    v.lbPad = s.zoom || lbNarrow ? '0' : '0 120px';
    v.lbCursor = s.zoom ? 'zoom-out' : 'zoom-in';
    v.lbOverflow = s.zoom ? 'auto' : 'hidden';

    var self2 = this, rank = 0;
    d.images.forEach(function (src, i) {
      /* hidden thumbnails for pictures of another colour or package; the shown ones
         number 01, 02, ... in order */
      if (d.imgTags) {
        var vis = self2.shows(i);
        if (vis) rank += 1;
        v['thumbDisp' + i] = vis ? 'flex' : 'none';
        v['lbTDisp' + i] = vis ? 'block' : 'none';
        v['thumbN' + i] = vis ? (rank < 10 ? '0' : '') + rank : '';
      }
      var on = i === s.img;
      v['thumbRule' + i] = on ? C.rule : C.none;
      v['thumbNum' + i] = on ? C.fg : C.thumbOff;
      v['thumbCls' + i] = on ? 'is-on' : '';
      var lbOn = v.lbMain && i === s.lbImg;
      v['lbBord' + i] = lbOn ? C.onDark : C.none;
      v['lbOp' + i] = lbOn ? '1' : '0.45';
    });
    (d.stripImages || []).forEach(function (src, i) {
      var on = v.lbStrip && i === s.lbImg;
      v['sbBord' + i] = on ? C.onDark : C.none;
      v['sbOp' + i] = on ? '1' : '0.45';
    });
    (d.reviewImages || []).forEach(function (src, i) {
      var on = v.lbReview && i === s.lbImg;
      v['rbBord' + i] = on ? C.onDark : C.none;
      v['rbOp' + i] = on ? '1' : '0.45';
    });

    /* ---- option pickers ---- */
    d.legs.forEach(function (l, i) {
      var c = cell(i === s.leg, i, true);
      Object.keys(c).forEach(function (k) { v['leg' + k + i] = c[k]; });
    });
    d.colors.forEach(function (col, i) {
      var c = cell(i === s.col, i, true);
      Object.keys(c).forEach(function (k) { v['col' + k + i] = c[k]; });
    });
    [1, 0].forEach(function (val, i) {
      var c = cell((topOn ? 1 : 0) === val, i, true);
      Object.keys(c).forEach(function (k) { v['top' + k + i] = c[k]; });
    });
    /* GUARDED, because `colors` is empty on every product without a Color
       option - which is 46 of the store's 51. An unguarded `.name` here threw
       on boot, and the throw killed the WHOLE configurator: no drawer, no
       stepper, no strikethrough, no savings row, nothing bound. The page still
       looked finished because Liquid had already rendered every one of those
       values server-side, so the only symptom was that nothing reacted.
       Same shape as the variant-map defect: five products worked, 46 did not,
       and the five were the ones anybody had open. */
    v.legName = d.legs[s.leg] || '';
    v.colName = (d.colors[s.col] || {}).name || '';
    /* THE NOTE SAYS SOMETHING IN BOTH STATES NOW. It was empty when the toppers
       were in, which on a segmented Yes/No was survivable because the highlighted
       cell said "Yes". A checkbox has no such word, so an empty note left the
       row saying nothing at all about what the tick was worth. */
    /* Ticked used to say nothing (Bryn, 2026-10-15: no "Included" echo); it now states the saving instead, below. */
    var topSave = d.topperDiscount || 0;
    if (d.hasNoTop) {
      var withTop = d.variants[s.leg + '|' + s.col];
      var noTop = d.variants[s.leg + '|' + s.col + '|n'];
      topSave = withTop && noTop ? withTop.price - noTop.price : 0;
    }
    /* Ticked: what adding them now saves against buying the topper set later,
       which also ships on its own (Josh, 2026-10-16). Whole dollars - it is a
       headline, not a line total. Silent when there is no saving to state. */
    var later = (d.topAlone || 0) - topSave;
    v.topNote = topOn
      ? (later >= 1 ? 'Save ' + fmt(Math.round(later), true) + ' + shipping' : '')
      : '−' + fmt(topSave);
    v.topNoteCol = topOn ? C.accent : 'var(--z-quiet)';
    v.topChkBg = topOn ? C.accent : C.pickBgOff;
    v.topChkBord = topOn ? C.accent : 'var(--z-check-border)';
    v.topTick = topOn ? '1' : '0';

    /* ---- the bundle item panel ---- */
    var bi = s.binfo >= 0 ? this.bItems[s.binfo] : null;
    v.binfoOpen = !!bi;
    v.binfoImg = bi ? bi.img : '';
    v.binfoTitle = bi ? bi.title : '';
    v.binfoSub = bi ? bi.sub : '';
    /* NOT INCLUDED, IN WORDS. The comparison table draws a dash for an
       accessory a bundle size does not carry, and a dash is easy to read past.
       The panel exists to answer "what am I actually getting", so it says so. */
    var bSizes = d.bundleSizes || [];
    bSizes.forEach(function (n) {
      var q = bi && bi['q' + n];
      v['binfoQ' + n] = bi ? (q ? '\u00d7' + q + ' included' : 'Not included') : '';
    });

    /* ---- bundle row ---- */
    [0].concat(bSizes).forEach(function (players, i) {
      var on = s.bundle === players;
      v['plBg' + i] = on ? C.accent : C.pickBgOff;
      v['plFg' + i] = on ? C.onDark : C.fgOff;
      v['plRule' + i] = on ? C.accent : C.none;
      v['plDiv' + i] = i === 0 ? C.none : C.div;
      v['plMl' + i] = i === 0 ? '0' : '-1px';
      v['dpBg' + i] = on ? C.fg : C.pickBgOff;
      v['dpFg' + i] = on ? C.onDark : C.fg;
      v['dpSubFg' + i] = on ? 'rgba(var(--z-on-dark-rgb), 0.7)' : C.fgOff;
      v['dpBord' + i] = on ? C.fg : C.div;
    });
    v.bundleAdded = bAdded;
    v.bundleNone = !bAdded;
    v.bundleName = bAdded
      ? d.bundleLabel + ' (' + s.bundle + ' players)'
      : d.bundleLabel;
    v.bundlePrice = bundle ? '+' + fmt(bundle.price) : '';
    v.bundleSave = bundle
      ? 'SAVE ' + fmt(Math.max(0, bundle.compareAt - bundle.price))
      : '';
    v.bundleFrom = d.bundleFrom;
    bSizes.forEach(function (n) {
      v['col' + n + 'Fg'] = s.bundle === n ? C.fg : C.mute;
      d.compare.forEach(function (c, i) {
        v['cmp' + n + 'Bg' + i] = s.bundle === n ? 'var(--z-tint)' : 'transparent';
      });
    });

    /* ---- seating row ---- */
    v.seatTag = chairPacks > 0
      ? s.chairs + ' chairs · +' + fmt(chairPacks * (d.seating ? d.seating.price : 0))
      : d.seatingTag;
    v.seatTagFg = s.chairs > 0 ? C.fgOff : C.onDark;
    v.seatTagBg = s.chairs > 0 ? 'transparent' : C.accent;
    v.chairsInOrder = s.chairs > 0;
    v.chairQty = s.draftChairs;
    v.chairImg = d.seating
      ? d.seating.images[s.col] || d.seating.images[0]
      : '';
    [0, 2, 4, 6, 8, 10].forEach(function (n, i) {
      var c = cell(s.draftChairs === n, i);
      Object.keys(c).forEach(function (k) { v['chq' + k + i] = c[k]; });
    });

    /* ---- essentials row ---- */
    v.essInOrder = essN > 0;
    v.essTag = essN > 0
      ? essN + (essN === 1 ? ' item · +' : ' items · +') + fmt(essT)
      : '';
    d.essentials.forEach(function (e, i) {
      var q = s.q[i] || 0;
      v['essQty' + i] = q;
      v['essBg' + i] = q > 0 ? C.essOn : C.essOff;
      v['essHover' + i] = q > 0 ? C.essOn : C.essHoverOff;
    });

    /* ---- drawer ---- */
    v.drawerOpen = s.drawer !== 0 || !!s.drawerClosing;
    v.drawerAnim = s.drawerClosing
      ? 'ttPdpDrawerOut var(--z-drawer-out) var(--z-drawer-ease) forwards'
      : 'ttPdpDrawerIn var(--z-drawer-in) var(--z-drawer-ease)';
    v.scrimAnim = s.drawerClosing
      ? 'ttPdpScrimOut var(--z-scrim-out) ease forwards'
      : 'ttPdpScrimIn var(--z-scrim-in) ease';
    v.isBundle = dk === 'bundle';
    v.isSeating = dk === 'seating';
    v.isEss = dk === 'ess';
    v.notBundle = dk === 'seating' || dk === 'ess';
    v.drawerW = dk === 'bundle' ? 'min(620px, 94vw)' : 'min(var(--z-sheet-w), 94vw)'; // the cart drawer reads the same token
    v.drawerTitle =
      dk === 'seating'
        ? 'Add Seating'
        : dk === 'ess'
          ? (d.essView ? 'The Essentials' : 'Add the Essentials') // view-only for distributor shoppers
          : 'Compare ' + (d.bundleLine || 'Signature') + ' Bundles';
    v.drawerAdded =
      dk === 'seating'
        ? chairPacks > 0 ? '+' + fmt(chairPacks * (d.seating ? d.seating.price : 0)) : '—'
        : essN > 0 ? '+' + fmt(essT) : '—';
    /* EVERY DRAWER GETS DONE. Josh, 2026-08-21: "Add seating drawer is not
       functioning the same as signature bundle... I think you should add the
       done button like signature bundle. Add done button to add the essentials
       as well."

       The button was already here and was being switched off for two of the
       three. The reasoning had been that seating and essentials commit on every
       tap so there is nothing left to confirm - true of the DATA and wrong about
       the person, who has just been given a panel with no way out except the ✕
       in its corner. Three drawers that behave alike is worth more than a button
       that is technically redundant. */
    v.ctaDisplay = 'block';

    /* ---- what it was, and what you save ----
       The pair tracks the VARIANT, not the product: a different finish can carry
       a different compare-at. Hidden whenever there is no real discount, so the
       page never strikes a price equal to the one beside it. */
    var wasEach = variant && variant.compareAt > variant.price ? variant.compareAt : 0;
    v.hasWas = wasEach > 0;
    v.wasPrice = wasEach ? fmt(wasEach * s.qty) : '';
    /* One string, one text node — the pill is an inline-flex and a whitespace-only
       node between two children is dropped, which is how it rendered SAVE$90.00. */
    v.wasSaved = wasEach ? 'Save ' + fmt((wasEach - variant.price) * s.qty) : '';
    /* No strike-through or saving at the top (Josh, 2026-10-19); the package's prices and
       saving live in the package rows. Since 2026-10-20 (drive first) the top price is the
       picked package's, so it, the subtotal and the cart agree. */
    if (d.drive) {
      (d.drive2 ? [0, 1, 2] : [0, 1]).forEach(function (val, i) {
        var c = cell(s.pkg === val, i, true);
        Object.keys(c).forEach(function (k) { v['pkg' + k + i] = c[k]; });
        /* the block style (package_style: blocks): outline, ground and tick */
        var on = s.pkg === val;
        v['pkgline' + i] = on ? 'var(--z-ink)' : 'var(--z-divider)';
        v['pkgfill' + i] = on ? 'var(--z-paper)' : 'var(--z-panel)';
        v['pkgbox' + i] = on ? 'var(--z-ink)' : 'var(--z-panel)';
        v['pkgboxb' + i] = on ? 'var(--z-ink)' : 'var(--z-check-border)';
        v['pkgtick' + i] = on ? '1' : '0';
      });
      /* What's included: the drive's lines show while the drive is picked */
      v.pkgDrive = s.pkg >= 1;
      v.pkgDrive2 = s.pkg === 2;
      v.pkgOne = s.pkg === 1;
      v.pkgHull = fmt(base);
      v.pkgWith = fmt(base + d.drive.price - d.drive.off);
      if (d.drive2) v.pkgWith2 = fmt(base + d.drive.price - d.drive.off + d.drive2.price - d.drive2.off);
    }

    /* the model chips, drawn like the package chips */
    if (d.models) {
      d.models.forEach(function (m, i) {
        if (i && !m) return;
        var c = cell((s.model || 0) === i, i, true);
        Object.keys(c).forEach(function (k) { v['mod' + k + i] = c[k]; });
        /* What's included: the picked model's list */
        v['model' + i] = (s.model || 0) === i;
      });
    }

    /* ---- quantity ---- */
    v.qtyVal = String(s.qty);
    v.qtyMinusOff = s.qty <= 1;

    /* ---- totals ---- */
    /* a boat's price at the top is the picked package's (2026-10-20, drive first) */
    v.tablePrice = fmt((base + drvEach) * s.qty);
    v.subtotal = fmt(sub);
    v.savings = fmt(save);
    v.savingsAmt = save > 0.005 ? '−' + fmt(save) : '$0.00';
    v.savingsFg = save > 0.005 ? C.amberText : C.mute;
    /* The Bundle savings LINE, not just its colour. It used to render at rest on
       every product with an add-on row, permanently reading $0.00 — a discount
       that failed to apply rather than a fact. Liquid ships it hidden; this is
       what brings it back the moment a bundle or seating is actually added. */
    v.hasSavings = save > 0.005;
    /* THE ROW HOLDS ITS SPACE EITHER WAY. Josh, 2026-08-21: the buy button
       dropped 39px the moment a bundle was added. `hasSavings` still decides
       whether the words are readable; it no longer decides whether the row
       exists, so nothing below it moves. See the note in sections/tt-pdp.liquid. */
    v.savingsVis = v.hasSavings ? 'visible' : 'hidden';
    /* "/mo" is INSIDE the <strong> in the mockup, not beside it - so it is part
       of the bound value rather than a sibling span. A sibling put an extra node
       in the tree and split the sentence across two boxes. */
    /* `monthly` is gone with the invented finance figure it fed - see the note
       in sections/tt-pdp.liquid where the Shop Pay line was. Shopify's own
       element states the real terms and updates itself; nothing here computes
       a payment plan any more. */

    /* ---- availability ----
       THREE STATES, AND PRE-ORDER IS NOT ONE INVENTORY CAN DECIDE. Josh,
       2026-08-21: the site claimed pre-order for products in stock today. This
       read `!variant.available` as pre-order, which says "coming soon" about
       something that is actually sold out - and said nothing at all about the
       fourteen products the merchandiser HAS marked, most of which are
       available and orderable right now.

       `stockState` comes from snippets/tt-stock-state.liquid, which reads
       theme.custom_badge. Sold-out is still inferred from the variant, because
       that genuinely is an inventory fact - but only for a product carrying no
       badge, so a Gamefound table with nothing in the warehouse still reads as
       the campaign it is rather than as a failure to restock. */
    /* Gamefound products are sold on the campaign: the CTA is a link out and
       nothing here says pre-order (Josh, 2026-10-15). */
    v.heroBadgeDisp = '';
    v.shipLineOn = false;
    v.shipLineText = '';
    if (d.stockState === 'gamefound') {
      v.stockBadge = d.preorderBadge;
      v.shipHeadline = 'Available through our Gamefound campaign';
      v.shipNote = v.shipHeadline;
      v.cta = 'Back it on Gamefound';
      v.ctaDisabled = false;
      return v;
    }
    /* A PRODUCT-LEVEL PREORDER BADGE YIELDS TO A VARIANT WITH STOCK (Bryn,
       2026-10-15): the 4x6 is badged Preorder for Dark Walnut, but the
       Driftwood Oak variant is on the shelf and must read In stock. */
    var variantInStock = !!(variant && variant.inv > 0);
    var badged = d.stockState === 'preorder' && !variantInStock;
    /* The photo pill follows the chosen colour: shown while that variant is on
       preorder, hidden once it is in stock (Bryn, 2026-10-17, Josh's pick; this
       replaces the 2026-10-16 rule that hid it whenever ANY colour was in stock,
       which took it off Dark Walnut because Driftwood Oak was on the shelf). */
    v.heroBadgeDisp = (d.stockState === 'preorder' && variantInStock) ? 'none' : '';
    /* The preorder ship line ("Preorders ship January") follows the same
       variant: shown while it has no stock (Josh, 2026-10-17). */
    /* Always present for a product with a ship line, so a finish change never
       adds or removes a bullet: "In stock" for a colour on the shelf (Josh,
       2026-10-19). */
    v.shipLineOn = !!d.hasShipLine;
    v.shipLineText = variantInStock ? 'In stock' : (d.shipLine || d.preorderText);
    /* A variant's own ship note wins (Josh, 2026-10-23): Counter and Coffee
       tables ship in January even while old leg stock reads as on hand. */
    if (variant && variant.ship) v.shipLineText = variant.ship;
    var soldOut = !badged && variant ? !variant.available : false;
    v.stockBadge = badged ? d.preorderBadge : (soldOut ? 'Sold out' : 'In stock');
    v.shipHeadline = badged ? d.preorderText : (soldOut ? d.soldOutText : d.inStockText);
    v.shipNote = v.shipHeadline;
    v.cta = s.adding ? 'Adding…' : (badged ? 'Pre-order Now' : (soldOut ? 'Sold out' : 'Add to Cart'));
    v.ctaDisabled = soldOut;

    return v;
  };

  /* The variant whose selectedOptions match the current leg + colour. The
     lookup key is built by Liquid so the two never drift. */
  Pdp.prototype.variant = function () {
    var key = this.state.leg + '|' + this.state.col;
    /* Unticked toppers on a table that has a real no-topper variant: that
       variant, so the cart, the price and the parts that ship all agree. */
    if (this.state.toppers === 0 && this.d.hasNoTop && this.d.variants[key + '|n']) key += '|n';
    return this.d.variants[key] || null;
  };

  /* The stepper buttons go through the action reducer; typing does not, so the
     field needs its own listener or the number on screen and the number in the
     cart would disagree. Clamped on `change` rather than on every keystroke so
     that clearing the box to retype does not snap it back to 1 mid-edit. */
  Pdp.prototype.bindQty = function () {
    var self = this;
    this.root.querySelectorAll('[data-tt-qty]').forEach(function (el) {
      el.addEventListener('input', function () {
        var n = parseInt(el.value, 10);
        if (isFinite(n) && n >= 1) self.set({ qty: Math.min(99, n) });
      });
      el.addEventListener('change', function () {
        var n = parseInt(el.value, 10);
        self.set({ qty: !isFinite(n) || n < 1 ? 1 : Math.min(99, n) });
      });
    });
  };

  Pdp.prototype.repaint = function () {
    var v = this.renderVals();

    this.binds.forEach(function (b) {
      var el = b.el;
      if (b.cond !== undefined) {
        var on = !!v[b.cond];
        el.hidden = !on;
        el.style.display = on ? b.show : 'none';
        if (!on) return;
      }
      if (b.bind !== undefined) {
        var t = v[b.bind];
        el.textContent = t === undefined || t === null ? '' : String(t);
      }
      if (b.value !== undefined) {
        var nv = v[b.value];
        nv = nv === undefined || nv === null ? '' : String(nv);
        /* Only when it differs: assigning .value on a focused field moves the
           caret to the end, so a shopper typing "12" would fight the repaint. */
        if (el.value !== nv) el.value = nv;
      }
      if (b.disabled !== undefined) el.disabled = !!v[b.disabled];
      if (b.label !== undefined) {
        var text = v[b.label];
        for (var n = 0; n < el.childNodes.length; n++) {
          if (el.childNodes[n].nodeType === 3) {
            el.childNodes[n].nodeValue = text == null ? '' : String(text);
            break;
          }
        }
      }
      if (b.html !== undefined) el.innerHTML = v[b.html] == null ? '' : v[b.html];
      if (b.cls !== undefined) el.className = (b.baseCls + ' ' + (v[b.cls] || '')).trim();
      if (b.styles) {
        b.styles.forEach(function (st) {
          var val = v[st.key];
          if (val === undefined) return;
          if (st.prop === 'src') el.setAttribute('src', val);
          else if (st.prop === 'ess-hover') el.style.setProperty('--ess-hover', val);
          else el.style.setProperty(st.prop, String(val));
        });
      }
    });

    document.body.style.overflow = this.state.lb || this.state.drawer ? 'hidden' : '';
    this.syncThumbs();
  };

  Pdp.prototype.syncThumbs = function () {
    var i = this.state.lb && this.state.lbSet === 'main' ? this.state.lbImg : this.state.img;
    this.root.querySelectorAll('[data-thumb="' + i + '"]').forEach(function (el) {
      var rail = el.parentElement;
      if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return;
      var target = el.offsetLeft - (rail.clientWidth - el.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    });
  };

  /* Every selection becomes a real cart line. The table first, so it heads the
     cart; then the bundle, the chairs, and each essential with a quantity. */
  Pdp.prototype.lines = function () {
    var s = this.state;
    var d = this.d;
    var items = [];
    var variant = this.variant();
    var model = this.state.model && d.models ? d.models[this.state.model] : null;
    if (model) items.push({ id: model.variant, quantity: s.qty });
    else if (variant) items.push({ id: variant.id, quantity: s.qty });
    /* The drive rides beside the boat, one per boat. */
    if (variant && d.drive && s.pkg) items.push({ id: d.drive.variant, quantity: s.qty });
    if (variant && d.drive2 && s.pkg === 2) items.push({ id: d.drive2.variant, quantity: s.qty });

    if (s.bundle) {
      var b = d.bundles[String(s.bundle)];
      var bv = b && (b.variants[s.col] || b.variants[0]);
      if (bv) items.push({ id: bv, quantity: 1 });
    }

    /* The toppers choice is a price change, not a SKU, so it travels as a line
       item property. Without it an order for a table at $1,499.99 is
       indistinguishable from one at $1,899.99 on the picking sheet. */
    if (d.topperDiscount > 0 && !d.hasNoTop && items.length) {
      items[0].properties = { 'Table toppers': s.toppers === 0 ? 'Not included' : 'Included' };
    }

    var packs = Math.floor(s.chairs / 2);
    if (packs > 0 && d.seating) {
      var sv = d.seating.variants[s.col] || d.seating.variants[0];
      if (sv) items.push({ id: sv, quantity: packs });
    }

    d.essentials.forEach(function (e, i) {
      var q = s.q[i] || 0;
      if (q > 0 && e.variant) items.push({ id: e.variant, quantity: q });
    });

    return items;
  };

  Pdp.prototype.submit = function (ev) {
    ev.preventDefault();
    if (this.state.adding) return;
    var self = this;
    var items = this.lines();
    if (!items.length) return;

    this.set({ adding: true });
    /* THE .js ENDPOINT, AND THE HTTP STATUS DECIDES. `routes.cart_add_url` is
       `/cart/add`, whose refusal body is `{"errors":"Cart Error"}` with no
       status in it - so the old `body.status >= 400` test read a refused add as
       success and sent the shopper to an empty cart (2026-10-19, a sold-out
       Essentials Bundle). `/cart/add.js` names the reason. */
    var addUrl = /\.js$/.test(this.d.cartAddUrl) ? this.d.cartAddUrl : this.d.cartAddUrl + '.js';
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      /* the drawer and the header count come back with the add (Dawn's section rendering) */
      body: JSON.stringify({ items: items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname })
    })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (body) {
          if (!r.ok || (body && body.status && body.status >= 400)) {
            throw new Error((body && (body.description || body.message || body.errors)) || 'Could not add to cart.');
          }
          /* THE DRAWER, NOT THE CART PAGE (Josh, 2026-10-19: "I hate being taken to the
             cart when I add items"). Dawn's cart drawer takes the sections that came
             back and slides open; its Check out goes straight to checkout. A store
             without the drawer still lands on the cart page. */
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && body.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(body);
            self.set({ adding: false });
            return;
          }
          window.location.href = self.d.cartUrl;
        });
      })
      .catch(function (err) {
        self.set({ adding: false });
        var box = self.root.querySelector('[data-tt-cart-error]');
        if (box) {
          box.textContent = (err && err.message) || 'Could not add to cart.';
          box.hidden = false;
          /* The span ships with an inline display:none, which `hidden = false`
             does not undo - so a refused add (a sold-out bundle, 2026-10-19)
             reset the button silently and the shopper found an empty cart. */
          box.style.display = 'block';
        }
      });
  };

  /* ONE SCROLL, TWO JUMPS. The offset is the sticky nav's height: without it a
     bare `#anchor` puts the heading underneath the bar, which is most of why a
     jump feels broken rather than merely abrupt. */
  function scrollToId(id) {
    var el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 90,
      behavior: 'smooth'
    });
  }

  function boot() {
    document.querySelectorAll('[data-tt-pdp]').forEach(function (root) {
      if (root.ttPdp) return;
      try {
        root.ttPdp = new Pdp(root);
      } catch (err) {
        /* Liquid already rendered a complete, readable page. A configurator
           failure must not take the product copy down with it. */
        if (window.console) console.error('tt-pdp:', err);
      }
    });
  }

  /* FEATURE PHOTO ROWS (snippets/tt-pdp-tiles, 2026-10-19). The arrows step the row
     one screen of photos at a time and wrap at the end; the counter follows the scroll.
     Written here rather than leaning on assets/tt-site.js, which a product page does
     not load - that is why the arrows did nothing. */
  function bootTiles() {
    document.querySelectorAll('[data-tt-tiles]').forEach(function (row) {
      if (row.ttTiles) return;
      row.ttTiles = true;
      var box = row.parentElement;
      var count = box.querySelector('[data-tt-tiles-count]');
      var items = row.children;
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      var stepW = function () {
        var first = items[0];
        return first ? first.getBoundingClientRect().width + (parseFloat(getComputedStyle(row).columnGap) || 0) : row.clientWidth;
      };
      var page = function () { return Math.max(1, Math.floor((row.clientWidth + 1) / stepW())); };
      var go = function (dir) {
        var atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
        if (dir > 0 && atEnd) return row.scrollTo({ left: 0, behavior: 'smooth' });
        if (dir < 0 && row.scrollLeft <= 4) return row.scrollTo({ left: row.scrollWidth, behavior: 'smooth' });
        row.scrollBy({ left: dir * stepW() * page(), behavior: 'smooth' });
      };
      var prev = box.querySelector('[data-tt-tiles-prev]');
      var next = box.querySelector('[data-tt-tiles-next]');
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });
      row.addEventListener('scroll', function () {
        if (count) count.textContent = pad(Math.min(items.length, Math.round(row.scrollLeft / stepW()) + 1)) + ' / ' + pad(items.length);
      }, { passive: true });
    });
  }
  document.addEventListener('shopify:section:load', bootTiles);

  /* RATED BARS FILL WITH THE SCROLL (2026-10-19, Josh: "Animate the how it handles
     and conditions color fill on scroll"). Each row's --f runs 0 to 1 as its list
     enters the screen, a row behind the one above it, full half a screen later.
     Reduce Motion leaves them full. */
  function bootBars() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var lists = [].slice.call(document.querySelectorAll('[data-tt-bars]'));
    if (!lists.length) return;
    var clamp = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };
    var tick = function () {
      var vh = window.innerHeight;
      lists.forEach(function (list) {
        /* Starts the moment the list's top enters the screen and is full half a
           screen later (Josh, 2026-10-19: "I don't like that I can see bar graphs
           without any color. The animation fill should start sooner"). */
        var p = clamp((vh - list.getBoundingClientRect().top) / (vh * 0.5));
        [].forEach.call(list.children, function (row, i) {
          row.style.setProperty('--f', clamp(p * 1.3 - i * 0.06).toFixed(3));
        });
      });
    };
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    tick();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootBars); else bootBars();

  /* HELD BLOCKS (sections/tt-pdp.liquid `hold`): a block that fits pauses under the
     header and title bar; a taller one pauses with its bottom 24px above the window's
     bottom, so none of it is held out of sight. */
  function bootHolds() {
    var holds = [].slice.call(document.querySelectorAll('[data-tt-hold]'));
    if (!holds.length) return;
    var fit = function () {
      var vh = window.innerHeight;
      /* never under the title bar or its progress strip (Josh, 2026-10-19: "There should be a
         gap on the bottom of the progress bar") - the bar is fixed, so its box is the same
         whether or not it is showing yet */
      var now = document.querySelector('[data-pdp-now]');
      var below = now ? Math.round(now.getBoundingClientRect().bottom) + (vh < 760 ? 16 : 28) : 0;
      holds.forEach(function (hold) {
        var el = hold.querySelector('.tt-pdp-hold__in');
        hold.style.removeProperty('--hold-top');
        var css = parseFloat(getComputedStyle(el).top) || 0;
        var want = Math.max(css, below);
        /* EVERY block pauses as it arrives, under the bar, tall ones included (Josh,
           2026-10-19: "Reviews block needs a pause on scroll" - pausing a tall block only
           once its bottom was on screen came too late to read as a pause). Its lower part
           scrolls into view after the pause. */
        if (want !== css) hold.style.setProperty('--hold-top', want + 'px');
      });
    };
    window.addEventListener('resize', fit);
    window.addEventListener('load', fit);
    fit();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootHolds); else bootHolds();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootTiles); else bootTiles();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
